import { useMemo, useEffect } from 'react';
import { standardAssetClasses } from "@/utils/asset-class";
import { logger } from "@/services/LoggingService";
import { getFundDisplayName } from "@/utils/fundDisplayUtils";

interface Allocation {
  id: string;
  asset_id: string;
  asset_type?: string;
  fund?: {
    fund_name?: string;
    investment_strategy?: string;
    id?: string;
  };
  allocation_percentage: number;
}

export function useAssetClassAllocations(portfolioAllocations: Allocation[] = []) {
  // Consolidate debug logging to run only when allocations change and only in DEV
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    logger.debug('useAssetClassAllocations', 'Processing allocations', { 
      count: portfolioAllocations.length,
      allocations: portfolioAllocations.map(a => ({
        id: a.id,
        asset_id: a.asset_id,
        asset_type: a.asset_type,
        allocation_percentage: a.allocation_percentage
      }))
    });

    if (portfolioAllocations.length > 0) {
      const classificationSummary = portfolioAllocations.reduce((summary, allocation) => {
        const assetType = (allocation.asset_type || 'unclassified').toLowerCase();
        summary[assetType] = (summary[assetType] || 0) + 1;
        return summary;
      }, {} as Record<string, number>);

      logger.debug('useAssetClassAllocations', 'Asset class distribution', {
        total: portfolioAllocations.length,
        distribution: classificationSummary
      });
    }
  }, [portfolioAllocations]);
  
  // Calculate allocated assets by standard asset class
  const allocatedAssets = useMemo(() => {
    return (standardAssetClasses || []).map(assetClass => {
      const allocations = (portfolioAllocations || [])
        .filter(allocation => {
          const allocationAssetClass = (allocation.asset_type || 'unclassified').toLowerCase();
          const target = assetClass.toLowerCase();
          // Merge 'unclassified' into 'multi_asset'
          if (target === 'multi_asset') {
            return allocationAssetClass === 'multi_asset' || allocationAssetClass === 'unclassified';
          }
          return allocationAssetClass === target;
        })
        .slice()
        .sort((a, b) => {
          const aName = (getFundDisplayName((a as any).fund) || a.asset_id || '').toString().toLowerCase();
          const bName = (getFundDisplayName((b as any).fund) || b.asset_id || '').toString().toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;
          return (a.id || '').localeCompare(b.id || '');
        });

      return {
        assetClass,
        allocations,
        totalAllocation: allocations.reduce((sum, allocation) => sum + allocation.allocation_percentage, 0),
      };
    });
  }, [portfolioAllocations]);

  // Function to get allocations for a specific asset class
  const getAllocationsForAssetClass = useMemo(() => {
    return (assetClass: string) => {
      const target = (assetClass || '').toLowerCase();
      const result = portfolioAllocations
        .filter(allocation => {
          if (!allocation) return false;
          if (!allocation.asset_type) {
            logger.warn('useAssetClassAllocations', 'Allocation missing asset_type', {
              allocationId: allocation.id,
              assetId: allocation.asset_id,
              allocation
            });
          }
          const actual = (allocation.asset_type || 'unclassified').toLowerCase();
          if (target === 'multi_asset') {
            return actual === 'multi_asset' || actual === 'unclassified';
          }
          return actual === target;
        })
        .slice()
        .sort((a, b) => {
          const aName = (getFundDisplayName((a as any).fund) || a.asset_id || '').toString().toLowerCase();
          const bName = (getFundDisplayName((b as any).fund) || b.asset_id || '').toString().toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;
          return (a.id || '').localeCompare(b.id || '');
        });

      if (import.meta.env.DEV) {
        logger.debug('useAssetClassAllocations', 'Filtered allocations by asset class (sorted alpha)', { 
          assetClass, 
          count: result.length
        });
      }
      return result;
    };
  }, [portfolioAllocations]);

  return { allocatedAssets, getAllocationsForAssetClass };
}
